import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["form", "taxToggle", "error"]
    static values = { sessionUrl: String }

    connect() {
        this._submittedOnce = false
        this._submitting = false
        this._restore = (event) => { if (event.persisted) this.resetSubmission() }
        window.addEventListener("pageshow", this._restore)
        this.resetSubmission()

        // Mark fields "touched" and validate-on-change/blur
        this.formTarget.querySelectorAll("input, select, textarea").forEach((el) => {
            el.addEventListener("blur",  () => { el._touched = true; this.validateField(el) })
            el.addEventListener("input", () => { if (el._touched) this.validateField(el) })
            el.addEventListener("change", () => { if (el._touched) this.validateField(el) })
        })

        // Keep amount error container hidden initially
        const amtErr = this.formTarget.querySelector("#amount-error")
        if (amtErr) amtErr.style.display = "none"
    }

    disconnect() {
        window.removeEventListener("pageshow", this._restore)
        this._refresh?.abort()
    }

    resetSubmission() {
        this._submitting = false
        this.formTarget.dispatchEvent(new CustomEvent("turbo:submit-end"))
        this.updateSubmitButtons(false)
    }

    updateSubmitButtons(busy) {
        // This form has separate desktop and mobile submit buttons.
        this.formTarget.querySelectorAll('[type="submit"]').forEach(button => {
            button.disabled = busy
            const text = button.querySelector('[data-form-loader-target="submitText"]')
            if (text) text.textContent = busy ? "Processing…" : "Complete RSVP"
            button.querySelector('[data-form-loader-target="spinner"]')?.classList.toggle("d-none", !busy)
        })
    }

    // ------ Actions ------------------------------------------------------------

    onGiftChange() {
        // Safely hide the amount error without destroying the DOM element
        this._showAmountError(false)

        this._clearField(this._el('input[name="paymethod"]'))
        ;["address","city","province","postcode"].forEach(n => this._clearField(this._el(`[name="${n}"]`)))
    }

    onTaxToggle() {
        // Clear address errors when toggle flips off
        if (!this.taxToggleTarget?.checked) {
            ;["address","city","province","postcode"].forEach(n => this._clearField(this._el(`[name="${n}"]`)))
        }
    }

    async onSubmit(e) {
        e.preventDefault()
        if (this._submitting) return
        this._submittedOnce = true

        const ok = this.validateAll({forceShow: true})
        if (!ok) {
            const needGift = this._isGiftYes()
            const hidden = this.formTarget.querySelector('[name="amount"]')
            const v = parseInt(hidden?.value || "0", 10) || 0
            this._showAmountError(needGift && v < 10)
            this.formTarget.querySelector('.is-invalid:not([type="hidden"])')?.scrollIntoView({block: "center"})
            return
        }

        // Refresh against the current cookie session before any payment POST.
        // Only this read-only GET is retriable; never replay a payment request.
        this._submitting = true
        if (this.hasErrorTarget) this.errorTarget.classList.add("d-none")
        this.formTarget.dispatchEvent(new CustomEvent("turbo:submit-start"))
        this.updateSubmitButtons(true)
        this._refresh = new AbortController()
        const timeout = window.setTimeout(() => this._refresh.abort(), 10000)
        try {
            const response = await fetch(this.sessionUrlValue, {
                credentials: "same-origin", cache: "no-store", redirect: "error",
                headers: { Accept: "application/json" }, signal: this._refresh.signal
            })
            if (!response.ok) throw new Error("Session refresh unavailable")
            const { csrf_token } = await response.json()
            const field = this.formTarget.querySelector('[name="authenticity_token"]')
            if (!field || typeof csrf_token !== "string" || !csrf_token) throw new Error("Missing form token")
            field.value = csrf_token
            HTMLFormElement.prototype.submit.call(this.formTarget)
        } catch (_) {
            if (!this.element.isConnected) return
            if (this.hasErrorTarget) {
                this.errorTarget.textContent = "We couldn’t connect just now. Your details are still here — please try again."
                this.errorTarget.classList.remove("d-none")
                this.errorTarget.focus()
            }
            this.resetSubmission()
        } finally {
            window.clearTimeout(timeout)
        }
    }

    // ------ Validation core ----------------------------------------------------

    validateAll({forceShow = false} = {}) {
        const needGift   = this._isGiftYes()
        const needTax    = needGift && this.taxToggleTarget?.checked

        const req = [
            '[name="first_name"]',
            '[name="last_name"]',
            '[name="email"]',
            '#terms'
        ]

        if (needGift) {
            req.push('[name="amount"]', 'input[name="paymethod"]')
            if (needTax) req.push('[name="address"]','[name="city"]','[name="province"]','[name="postcode"]')
        }

        let allValid = true
        req.forEach(sel => {
            const el = this._el(sel)
            if (!el) return
            if (!this.validateField(el, {forceShow})) allValid = false
        })

        // Hide amount error if gift is NO
        if (!needGift) this._showAmountError(false)

        return allValid
    }

    validateField(el, {forceShow = false} = {}) {
        if (!el) return true

        const name = this._nameFor(el)
        const value = this._valueFor(el)
        const showing = forceShow || this._submittedOnce || el._touched

        // Base requireds
        if (name === "first_name" || name === "last_name") {
            return this._req(el, value, "This field is required", {show: showing})
        }
        if (name === "email") {
            if (!this._req(el, value, "Email is required", {show: showing})) return false
            const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
            return this._flag(el, ok, "Enter a valid email", {show: showing})
        }
        if (name === "terms" && el.type === "checkbox") {
            return this._flag(el, el.checked, "You must agree to the terms", {show: showing})
        }

        // Conditional: gift
        if (this._isGiftYes()) {
            if (name === "amount") {
                const v = parseInt(value || "0", 10) || 0
                const ok = v >= 10
                // Show message inside the #amount-error container
                this._showAmountError(showing && !ok, "Please select or enter an amount (minimum $10)")
                // also set invalid class on a hidden proxy element (the container) to help scrollIntoView if needed
                return this._flag(this._amountProxy(), ok, "", {show: showing})
            }

            if (name === "paymethod") {
                const checked = !!this._el('input[name="paymethod"]:checked')
                return this._flag(el, checked, "Please select a payment method", {show: showing})
            }

            if (this.taxToggleTarget?.checked && ["address","city","province","postcode"].includes(name)) {
                return this._req(el, value, `${this._title(name)} is required`, {show: showing})
            }
        } else {
            // Gift = NO: clear contextual errors
            if (name === "amount") this._showAmountError(false)
        }

        // Not a validated field
        this._clearField(el)
        return true
    }

    onAmountChanged() {
        const proxy = this.formTarget.querySelector("#amount-error") || this.formTarget.querySelector('[name="amount"]')
        if (!proxy) return
        proxy._touched = true

    }

    // ------ Helpers ------------------------------------------------------------

    _isGiftYes() {
        const r = this.formTarget.querySelector('input[name="gift"]:checked')
        return r?.value === "yes"
    }

    _el(sel) {
        return this.formTarget.querySelector(sel)
    }

    _nameFor(el) {
        if (el.name) return el.name
        if (el.matches('#terms')) return 'terms'
        if (el.matches('input[name="paymethod"]')) return 'paymethod'
        return el.getAttribute('name') || ""
    }

    _valueFor(el) {
        if (el.type === "checkbox") return el.checked ? "on" : ""
        if (el.type === "radio") {
            const checked = this.formTarget.querySelector(`input[name="${el.name}"]:checked`)
            return checked ? checked.value : ""
        }
        return (el.value || "").trim()
    }

    _title(name) {
        return name.charAt(0).toUpperCase() + name.slice(1)
    }

    _amountProxy() {
        // use the error container as the “field” to toggle invalid styling contextually
        return this._el("#amount-error") || this._el('[name="amount"]')
    }

    _req(el, value, msg, {show}) {
        const ok = value.length > 0
        return this._flag(el, ok, msg, {show})
    }

    _flag(el, ok, msg, {show}) {
        if (!el) return true
        if (ok) {
            this._clearField(el)
            return true
        }
        if (show) {
            // amount uses a special container; everything else gets sibling .invalid-feedback
            if (el.id === "amount-error") {
                this._showAmountError(true, msg)
            } else {
                this._showError(el, msg)
            }
            el.classList.add("is-invalid")
        }
        return false
    }

    _clearField(el) {
        if (!el) return
        el.classList?.remove('is-invalid');

        // Remove error after the input, UNLESS it is the hardcoded #amount-error container
        const afterInput = el.nextElementSibling;
        if (afterInput?.classList?.contains('invalid-feedback') && afterInput.id !== "amount-error") {
            afterInput.remove();
        }

        // If checkbox, also remove error after its label
        if (el.matches('input[type="checkbox"]')) {
            const label = el.id
                ? el.closest('.form-check')?.querySelector(`label[for="${el.id}"]`)
                : null;
            const afterLabel = label?.nextElementSibling;
            if (afterLabel?.classList?.contains('invalid-feedback') && afterLabel.id !== "amount-error") {
                afterLabel.remove();
            }
        }
    }

    _showError(el, msg) {
        const isCheckbox = el.matches('input[type="checkbox"]');

        // Find where to place the error
        let anchor = el;
        if (isCheckbox) {
            // Prefer an explicit label[for] match inside the same .form-check
            const byFor = el.id
                ? el.closest('.form-check')?.querySelector(`label[for="${el.id}"]`)
                : null;
            // Fallback: immediate next sibling if it's a label
            const nextIsLabel = el.nextElementSibling?.tagName === 'LABEL' ? el.nextElementSibling : null;
            anchor = byFor || nextIsLabel || el;
        }

        // De-dupe: if an error is already right after the anchor, update it
        const sib = anchor.nextElementSibling;
        if (sib && sib.classList?.contains('invalid-feedback')) {
            sib.textContent = msg;
            return;
        }

        // Insert a fresh error
        const div = document.createElement('div');
        div.className = 'invalid-feedback d-block mt-1';
        div.textContent = msg;
        anchor.after(div);
    }

    _showAmountError(show, msg = "Please select or enter an amount (minimum $10)") {
        const box = this.formTarget.querySelector('#amount-error');
        if (!box) return;
        if (show) {
            box.textContent = msg;
            box.style.display = "";
        } else {
            box.textContent = "";
            box.style.display = "none";
        }
    }
}
